import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { PackLine } from '../../../services/fulfillment/types';

interface Props {
  line: PackLine;
  actions?: React.ReactNode;
  note?: React.ReactNode;
}

const ItemTypeIcon: React.FC<{ itemType: PackLine['itemType']; size: number }> = ({
  itemType,
  size,
}) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    xmlns: 'http://www.w3.org/2000/svg',
  };
  if (itemType === 'bike') {
    return (
      <svg {...common}>
        <path d="M9,14h-2v2h2v-2Z" fill="currentColor" />
        <path d="M17.01,17h-2.02v2.02h2.02v-2.02Z" fill="currentColor" />
        <path
          d="M19,7v-1c0-1.66-1.34-3-3-3H8c-1.66,0-3,1.34-3,3v1"
          fill="none"
          stroke="currentColor"
          strokeMiterlimit="10"
          strokeWidth="2"
        />
        <path d="M12,8V2" fill="none" stroke="currentColor" strokeMiterlimit="10" strokeWidth="2" />
        <path
          d="M16,13v-1c0-2.21-1.79-4-4-4s-4,1.79-4,4v1"
          fill="none"
          stroke="currentColor"
          strokeMiterlimit="10"
          strokeWidth="2"
        />
        <path
          d="M12,12v9"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeMiterlimit="10"
          strokeWidth="2"
        />
      </svg>
    );
  }
  if (itemType === 'frame') {
    return (
      <svg {...common}>
        <path
          d="M6.01,9.5L15,5l1,1-4.96,12.91s-.07,.04-.09,0l-5.32-7.98c-.33-.5-.15-1.17,.38-1.44Z"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeMiterlimit="10"
          strokeWidth="2"
        />
        <path d="M7.93,18H3.8s1.99-3.2,1.99-3.2l-1.22-1.83-2.47,3.97c-.38,.62-.4,1.39-.05,2.03,.35,.64,1.02,1.03,1.75,1.03h5.46l-1.33-2Z" />
        <polygon points="14.45 3.04 13.71 2.3 12.3 3.71 12.57 3.98 14.45 3.04" />
        <polygon points="17.94 6.53 17.16 8.57 21.23 12.64 22.64 11.23 17.94 6.53" />
      </svg>
    );
  }
  if (itemType === 'wheel') {
    return (
      <svg {...common}>
        <circle
          cx="10"
          cy="14"
          r="7"
          fill="none"
          stroke="currentColor"
          strokeMiterlimit="10"
          strokeWidth="2"
        />
        <path d="M18.14,2.56c-1.59-.71-3.36-.75-4.97-.12-1.31,.5-2.38,1.41-3.12,2.57,.76,0,1.53,.11,2.28,.31,.44-.44,.96-.79,1.55-1.02,.53-.2,1.07-.3,1.62-.3,.62,0,1.24,.13,1.82,.39,2.27,1.01,3.29,3.67,2.29,5.94-.23,.51-.54,.95-.92,1.34,.2,.75,.3,1.53,.31,2.32,1.07-.68,1.92-1.66,2.44-2.84,1.45-3.28-.03-7.12-3.3-8.58Z" />
        <path d="M16.74,8.05s0-.03,0-.05c0-.41-.34-.75-.75-.75-.01,0-.02,0-.04,0,.28,.25,.53,.51,.78,.79Z" />
        <circle cx="10" cy="14" r="1" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M8.5,23c.83,0,1.5-.67,1.5-1.5s-.67-1.5-1.5-1.5-1.5,.67-1.5,1.5,.67,1.5,1.5,1.5Z" />
      <path
        d="M21,17H7L4,2H1"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeWidth="2"
      />
      <path
        d="M5,6H22l-2,7H6"
        fill="none"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeWidth="2"
      />
      <path d="M18.5,23c.83,0,1.5-.67,1.5-1.5s-.67-1.5-1.5-1.5-1.5,.67-1.5,1.5,.67,1.5,1.5,1.5Z" />
    </svg>
  );
};

const PackLineItem: React.FC<Props> = ({ line, actions, note }) => {
  const thumbnail = (
    <Box
      sx={{
        width: 80,
        height: 80,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'grey.400',
      }}
    >
      {line.imageUrl ? (
        <img
          src={line.imageUrl}
          alt={line.sku}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        <ItemTypeIcon itemType={line.itemType} size={48} />
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', width: '100%' }}>
      {line.imageUrl ? (
        <Tooltip
          title={
            <img
              src={line.imageUrl}
              alt={line.sku}
              style={{ width: 320, height: 320, objectFit: 'contain', display: 'block' }}
            />
          }
          placement="right"
          componentsProps={{
            tooltip: {
              sx: { bgcolor: 'background.paper', p: 0.5, maxWidth: 'none', boxShadow: 3 },
            },
          }}
        >
          {thumbnail}
        </Tooltip>
      ) : (
        thumbnail
      )}

      {/* Details */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '0.3px', mb: 0.125 }}>
          {line.sku}
        </Typography>

        <Typography sx={{ fontSize: 13, color: 'grey.600' }} noWrap>
          {line.title}
        </Typography>

        {line.upc && (
          <Typography sx={{ fontSize: 12, mt: 0.25 }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}>
              UPC
            </Box>
            <Box component="span" sx={{ color: 'grey.500' }}>
              {line.upc}
            </Box>
          </Typography>
        )}

        {line.mpn && (
          <Typography sx={{ fontSize: 12 }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}>
              MPN
            </Box>
            <Box component="span" sx={{ color: 'grey.500' }}>
              {line.mpn}
            </Box>
          </Typography>
        )}

        {note}
      </Box>

      {/* Right-side actions */}
      {actions && (
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{actions}</Box>
      )}
    </Box>
  );
};

export default PackLineItem;
